import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';
import React, { UIEvent, useEffect, useState } from 'react';
import { isEmpty, isFunction, isNull } from 'lodash';

import { AddComponentContainer } from './AddComponentContainer';
import { AnchorSidePanel } from './AnchorSidePanel';
import Constants from '../constants';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButton } from '@mui/material';
import { LessonComponentBase } from '../models/LessonComponent';
import LessonComponentService from '../services/LessonComponentService';
import { LessonComponentType } from '../models/LessonComponentType';
import { LessonComponentView } from './LessonComponentView';
import MenuIcon from '@mui/icons-material/Menu';
import styles from '../styles/LessonComponentList.module.css';
import stylesSidePanel from '../styles/AnchorSidePanel.module.scss';
import { useLessonContext } from '../../content/models/LessonContext';
import { useLessonEditContext } from '../models/LessonEditContext';

interface LessonComponentListProps {
  components: LessonComponentBase[];
  editComponents?: LessonComponentBase[]; //* not required in 'edit' mode
  lessonTitle?: string;
  goToQuestion?: (id: number) => void;
  onLockChange?: (anyLocked: boolean) => void; //* not used in edit mode
  onSetup?: (component: LessonComponentBase) => void;
  onRequired?: (componentkey: number, required: boolean) => void;
  onDuplicate?: (componentIndex: number, multipleCopy?: boolean) => void;
  onDelete?: (componentkey: number) => void;
  onArrange?: (sourceIndex: number, destinationIndex: number) => void;
  onSelected?: (componentkey: number, selected: boolean) => void;
  onResponse?: (
    componentkey: number,
    response: string,
    points?: number,
    correct?: boolean
  ) => void;
  onChangeAnswer?: (componentKey: number, userAnswer: string) => void;
  onUpdateMark?: (componentKey: number, mark: number) => void; //* used only in 'mark' display mode
  onCreate?: (componentIndex: number) => void;
  onMultipleMove?: (componentIndex: number) => void;
}

export function LessonComponentList(props: LessonComponentListProps) {
  const { mode, lessonType, finalized } = useLessonContext();
  const [componentKey, setComponentKey] = useState<number>(null);
  const [lockingComponentPosition, setlockingComponentPosition] = useState(-1);
  const { selectedComponentKeys } = useLessonEditContext();
  const [openSidePanel, setOpenSidePanel] = useState<boolean>(false);

  useEffect(() => {
    if (isFunction(props.onLockChange)) {
      props.onLockChange(
        //* telling the parent component (LessonPlayer) that if any components are locked.
        lockingComponentPosition < props.components.length &&
          lockingComponentPosition !== -1
      );
    }
  }, [lockingComponentPosition]);

  useEffect(() => {
    if (props.components && mode !== 'edit') {
      setlockingComponentPosition(getLockPosition());
    }
  }, [props.components]);

  const getLockPosition = () => {
    if (LessonComponentService.canSubmit(mode))
      return LessonComponentService.getLockPosition(
        props.components,
        lessonType
      );
  };

  const handleResponse = (
    componentkey: number,
    required: boolean,
    componentPosition: number,
    response: string,
    type: LessonComponentType,
    points?: number,
    correct?: boolean
  ) => {
    setlockingComponentPosition(getLockPosition());
    if (isNull(correct) && LessonComponentService.isAutoMarking(type)) {
      return;
    }
    if (isFunction(props.onResponse)) {
      props.onResponse(componentkey, response, points, correct);
    }
  };

  const isComponentLocked = (index: number) => {
    return (
      mode !== 'edit' &&
      lockingComponentPosition >= 0 &&
      index > lockingComponentPosition
    );
  };

  const handleBeforeClick = (componentKey: number) => {
    setComponentKey(componentKey);
  };

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    console.debug(event);
  }

  const getDroppableStyle = (isDraggingOver: boolean) => {
    return {
      background: isDraggingOver ? '#eee' : 'transparent'
    };
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    props.onArrange(
      result.source.index, //* source index
      result.destination?.index //* destination index
    );
  };

  const handleGoToQuestion = (id: number) => {
    props.goToQuestion(id);
    setOpenSidePanel(false);
  };

  const sidePanelComponents = props.components?.filter(
    (c, index) =>
      LessonComponentService.canAddToSidePanel(c.type) &&
      !isComponentLocked(index)
  );

  const selectedComponents = props.components?.filter((c) =>
    selectedComponentKeys?.includes(c.componentkey)
  );
  const componentPositions: number[] = selectedComponents?.map(
    (c) => c.position
  );
  const invalidMove = (destinationIndex: number) => {
    return componentPositions.some(
      (position) =>
        destinationIndex === position || destinationIndex === position + 1
    );
  };

  return (
    <>
      {/** Side panel applicable for learner, preview and be displayed
       *  prior to finializing/submitting (expanded mode) */}
      {LessonComponentService.canSubmit(mode) &&
        !finalized &&
        !isEmpty(sidePanelComponents) && (
          <>
            <div className={stylesSidePanel.btnSidePanelContainer}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={() => setOpenSidePanel(!openSidePanel)}
                edge="start"
                className={`${stylesSidePanel.btnSidePanel} ${stylesSidePanel.iconSize}`}
              >
                <MenuIcon />
              </IconButton>
            </div>
            {openSidePanel && (
              <AnchorSidePanel
                components={sidePanelComponents}
                lessonTitle={props.lessonTitle}
                goToQuestion={handleGoToQuestion}
                onClose={() => setOpenSidePanel(false)}
              />
            )}
          </>
        )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="component-list" ignoreContainerClipping>
          {(provided1, snapshot1) => (
            <div
              className={styles.componentList}
              ref={provided1.innerRef}
              style={getDroppableStyle(snapshot1.isDraggingOver)}
              onScroll={handleScroll}
            >
              {props.components?.map((component, index) => {
                if (
                  finalized &&
                  !LessonComponentService.isInteractive(component)
                ) {
                  //* The summary view shows just interactive components once finalized.
                  return null;
                }
                if (isComponentLocked(index) && isComponentLocked(index - 1)) {
                  // Display only the first locked component.
                  return null;
                }

                return (
                  component?.componentkey !== Constants.InvalidComponentkey &&
                  component?.content && (
                    <Draggable
                      key={component.componentkey}
                      draggableId={`${component.componentkey}`}
                      index={index}
                      isDragDisabled={mode !== 'edit'}
                    >
                      {(provided2) => (
                        <Grid
                          container
                          sx={{ width: '100%' }}
                          className={
                            LessonComponentService.isFullwidthComponent(
                              component.type
                            ) ||
                            LessonComponentService.isStretchBackground(
                              component
                            )
                              ? styles.componentContainerFullWidth
                              : styles.componentContainer
                          }
                          ref={provided2.innerRef}
                          {...provided2.draggableProps}
                          alignItems="center"
                          data-componentkey={component.componentkey}
                        >
                          {mode === 'edit' && (
                            <Grid {...provided2.dragHandleProps}>
                              <DragIndicatorIcon />
                            </Grid>
                          )}
                          <Grid
                            style={{
                              flex: 1,
                              width: '100%'
                            }}
                          >
                            {mode === 'edit' && (
                              <AddComponentContainer
                                invalidMove={invalidMove(index)}
                                componentIndex={index}
                                onCreate={() => props.onCreate(index)}
                                onDuplicate={() =>
                                  props.onDuplicate(index, true)
                                }
                                onMultipleMove={() =>
                                  props.onMultipleMove(index)
                                }
                              />
                            )}
                            <div key={component.componentkey}>
                              <LessonComponentView
                                locked={isComponentLocked(index)}
                                component={component}
                                editComponent={props.editComponents?.find(
                                  (c) =>
                                    c.componentkey === component.componentkey
                                )} /* editComponent should be sync with the saved component */
                                editing={
                                  component.componentkey === componentKey
                                }
                                onSetup={props.onSetup}
                                onRequired={props.onRequired}
                                onDuplicate={() => props.onDuplicate(index)}
                                onDelete={props.onDelete}
                                onSelected={props.onSelected}
                                onResponse={(
                                  required,
                                  position,
                                  response,
                                  type,
                                  points,
                                  correct
                                ) =>
                                  handleResponse(
                                    component.componentkey,
                                    required,
                                    position,
                                    response,
                                    type,
                                    points,
                                    correct
                                  )
                                }
                                onBeforeClick={handleBeforeClick}
                                onChangeAnswer={props.onChangeAnswer}
                                onUpdateMark={props.onUpdateMark}
                              />
                            </div>
                            {mode === 'edit' &&
                              index === props.components.length - 1 && (
                                <AddComponentContainer
                                  invalidMove={invalidMove(index + 1)}
                                  componentIndex={index}
                                  onCreate={() => props.onCreate(index + 1)}
                                  onDuplicate={() =>
                                    props.onDuplicate(index + 1, true)
                                  }
                                  onMultipleMove={() =>
                                    props.onMultipleMove(index + 1)
                                  }
                                />
                              )}
                          </Grid>
                        </Grid>
                      )}
                    </Draggable>
                  )
                );
              })}
              {provided1.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
}
